import React, {useEffect, useState} from 'react'
import 'react-perfect-scrollbar/dist/css/styles.css'
import {useNavigate, Link, useLocation} from 'react-router-dom'
import {useSelector, useDispatch} from 'react-redux'
import {getUserName, getRole, getMerchant, isAuthorized, isEmailDomainAllowed} from '../helpers/ReduxHelpers'
import {isConfigRouteVisible} from '../helpers/configVisibility'
import {useIsMobileFrame} from '../theme/responsive.config'
import 'intro.js/introjs.css'
import {Steps} from 'intro.js-react'
import {apiConstants, post} from '../common/constants'
import _ from 'lodash'
import {Box, Collapse, Drawer, InputBase, List, ListItem, ListItemIcon, ListItemText, Typography, styled} from '@mui/material'
import {ExpandLess, ExpandMore, FiberManualRecord, Search as SearchIcon, Close as CloseIcon, UnfoldMore, UnfoldLess} from '@mui/icons-material'
import {useSidebarToggle} from '../components/SidebarToggleContext'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import {getSidebarItems} from '../../routes/routes.config'
import {buildMenuSearchIndex, searchMenu} from '../../routes/menuSearch'
import {TEAL} from '../styles/foundation/colors'
import useDirectPlanActive from '../components/directPlan/useDirectPlanActive'

// Sidebar palette — kept identical to the admin panel (proship-backend SidebarNew).
const DRAWER_BG_COLOR = '#161C24'
// Themed via CSS custom properties so a product can re-skin the sidebar without forking
// it. Nothing sets them today — the one product that did has been removed — so every
// fallback applies and these read as the plain merchant-panel values. Kept as the seam
// they are: re-skinning later is setting four variables, not editing five styled
// components.
const SIDEBAR_TEXT = 'var(--ps-sidebar-text, #ADB8C4)'
const SIDEBAR_ACCENT = 'var(--ps-sidebar-accent, #2065D1)'
const SIDEBAR_HOVER_BG = 'var(--ps-sidebar-hover, rgba(145,158,171,0.08))'
const SIDEBAR_ACTIVE_BG = 'var(--ps-sidebar-active, rgba(32,101,209,0.12))'
const SIDEBAR_LINE = 'var(--ps-sidebar-line, rgba(145,158,171,0.15))'

const StyledDrawer = styled(Drawer)(({drawerwidth}) => ({
  backgroundColor: DRAWER_BG_COLOR,
  '& .MuiDrawer-paper': {
    transition: 'width 0.2s ease-in-out',
    width: drawerwidth,
    overflowX: 'hidden',
    backgroundColor: DRAWER_BG_COLOR,
    color: SIDEBAR_TEXT,
    height: '100%',
    overflowY: 'auto',
    visibility: 'visible',
    // Slim, integrated scrollbar — transparent track, translucent rounded thumb
    // in the muted sidebar text colour; brightens on hover. Firefox via scrollbar-*.
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(145,158,171,0.3) transparent',
    '&::-webkit-scrollbar': {
      width: 6,
      background: 'transparent',
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(145,158,171,0.3)',
      borderRadius: '10px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(145,158,171,0.55)',
    },
  },
}))

const StyledList = styled(List)({
  backgroundColor: DRAWER_BG_COLOR,
  visibility: 'visible',
  '& .menu-items:hover': {
    '& + .MuiDrawer-paper': {
      width: '248px !important',
    },
  },
})

const HeaderListItem = styled(ListItem)(({theme}) => ({
  position: 'sticky',
  top: 0,
  background: DRAWER_BG_COLOR,
  paddingTop: theme.spacing(1.5),
  paddingBottom: theme.spacing(1.5),
  zIndex: theme.zIndex.drawer + 1,
}))

const ToggleIcon = styled('div')({
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out',
  color: SIDEBAR_TEXT,
  '&:hover': {
    color: SIDEBAR_ACCENT,
  },
})

const MenuItemsContainer = styled('div')(({drawerwidth}) => ({
  width: drawerwidth,
  transition: 'width 0.2s ease-in-out',
  '&:hover': {
    width: drawerwidth,
  },
}))

const MainMenuItem = styled(ListItem, {
  shouldForwardProp: (prop) => !['isactive', 'isselected'].includes(prop),
})(({isactive, isselected}) => {
  const active = isactive || isselected
  return {
    display: 'flex',
    justifyContent: 'space-between',
    color: active ? '#fff' : SIDEBAR_TEXT,
    width: 'auto',
    margin: '0 8px 2px',
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 10,
    paddingRight: 8,
    minHeight: 34,
    borderRadius: '6px',
    backgroundColor: active ? SIDEBAR_ACTIVE_BG : 'transparent',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '& .MuiListItemText-root': {
      transition: 'opacity 0.2s ease-in-out',
    },
    '&:hover': {
      backgroundColor: active ? SIDEBAR_ACTIVE_BG : SIDEBAR_HOVER_BG,
      color: active ? '#fff' : SIDEBAR_ACCENT,
    },
  }
})

const MainMenuIcon = styled(ListItemIcon, {
  shouldForwardProp: (prop) => !['isactive', 'isselected'].includes(prop),
})(({isactive, isselected}) => ({
  minWidth: 28,
  '& svg': {
    fontSize: '16px',
  },
  color: isactive || isselected ? '#fff' : SIDEBAR_TEXT,
}))

const MenuText = styled(ListItemText)({
  marginLeft: 8,
  '& .MuiTypography-root': {
    fontFamily: 'Poppins, sans-serif',
  },
})

const ExpandIcon = styled(ListItemIcon)({
  display: 'flex',
  justifyContent: 'flex-end',
})

// Submenu list: a thin vertical guide line down the left edge (like the admin panel),
// instead of relying on a column of bullet dots for grouping.
const SubMenuList = styled(List)({
  marginLeft: '20px',
  paddingLeft: '12px',
  paddingTop: 0,
  paddingBottom: 0,
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    width: '1px',
    backgroundColor: SIDEBAR_LINE,
  },
})

const SubMenuItem = styled(ListItem, {
  shouldForwardProp: (prop) => prop !== 'isactive',
})(({isactive}) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  color: isactive ? '#fff' : SIDEBAR_TEXT,
  paddingTop: 3,
  paddingBottom: 3,
  paddingLeft: 8,
  marginRight: 8,
  borderRadius: '6px',
  minHeight: 26,
  transition: 'background-color 0.15s ease, color 0.15s ease',
  '&:hover': {
    backgroundColor: SIDEBAR_HOVER_BG,
    color: SIDEBAR_ACCENT,
    '& svg': {color: SIDEBAR_ACCENT},
  },
}))

const SubMenuText = styled(ListItemText)({
  marginLeft: 0,
  '& .MuiTypography-root': {
    fontSize: '11px !important',
    fontFamily: 'Poppins, sans-serif !important',
    lineHeight: '1.3 !important',
  },
})

const BadgeSpan = styled('span')({
  backgroundColor: '#29ff65',
  color: '#000',
  padding: '2px 8px',
  borderRadius: '12px',
  fontSize: '10px',
  fontWeight: 'bold',
  textTransform: 'uppercase',
})

const SectionLabel = styled('div')(({drawerwidth}) => {
  const collapsed = drawerwidth === '60px'
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    margin: collapsed ? '4px 8px 0' : '18px 8px 6px',
    padding: collapsed ? 0 : '8px 12px 0',
    borderTop: collapsed ? 'none' : `1px solid ${SIDEBAR_LINE}`,
    height: collapsed ? 0 : 'auto',
    overflow: 'hidden',
    opacity: collapsed ? 0 : 1,
    transition: 'opacity 0.2s ease-in-out',
    whiteSpace: 'nowrap',
    userSelect: 'none',
  }
})

const SectionLabelText = styled('span')({
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.1em',
  textTransform: 'uppercase',
  fontFamily: 'Poppins, sans-serif',
  color: '#78838f',
})

// Search result row — a flat list item carrying its own icon and a parent breadcrumb,
// since results are no longer nested under a visible parent header.
const ResultItem = styled(ListItem)({
  display: 'flex',
  alignItems: 'center',
  color: SIDEBAR_TEXT,
  width: 'auto',
  margin: '0 8px 2px',
  paddingTop: 4,
  paddingBottom: 4,
  paddingLeft: 10,
  paddingRight: 8,
  minHeight: 34,
  borderRadius: '6px',
  transition: 'background-color 0.15s ease, color 0.15s ease',
  '&:hover': {
    backgroundColor: SIDEBAR_HOVER_BG,
    color: SIDEBAR_ACCENT,
    '& svg': {color: SIDEBAR_ACCENT},
  },
})

const SIDEBAR_GROUPS = [
  {key: 'AI_TOOLS', label: 'AI TOOLS', accent: '#7c4dff'},
  {key: 'GENERAL', label: 'GENERAL', accent: '#3783e7'},
  {key: 'D2C_FREIGHT', label: 'D2C FREIGHT', accent: '#E65100'},
  {key: 'PTL_FREIGHT', label: 'PTL FREIGHT', accent: '#283593'},
  // ProShip Direct v2 sits after PTL Freight: it is a plan a merchant activates on top of the
  // freight verticals, not a vertical of its own. Renders nothing when REACT_APP_DIRECT_PLAN is
  // off, because the group loop below skips groups with no visible items. Accent comes from the
  // token file rather than a literal, per UI_CODING_STANDARDS 2 — TEAL rather than the purple
  // above so the two Direct modules stay visually distinct while both flags are on.
  {key: 'DIRECT_PLAN', label: 'PROSHIP DIRECT', accent: TEAL},
  {key: 'ACCOUNT', label: 'ACCOUNT & COMMON', accent: '#637381'},
  {key: 'SUPPORT', label: 'UPDATES & ALERTS', accent: '#637381'},
]

const Sidebar = () => {
  const {pathname} = useLocation()
  // A live Direct subscription, not merely the module being built. Subscribed, so activating
  // a plan grows the nav immediately rather than at the next reload.
  const directPlanActive = useDirectPlanActive()
  let navigate = useNavigate()
  const dispatch = useDispatch()
  const {userInfo} = useSelector((state) => state.user)
  const userName = getUserName()
  const {orderConversionDetails} = useSelector((state) => state.report)

  const [enabled, setEnabled] = useState(false)
  const [initialStep] = useState(0)
  const [doneLabel] = useState('Finish')
  const accessList = getRole()
  let VerifiedRole = _.intersection(accessList, ['ROLE_PROSHIPADMIN', 'ROLE_ADMIN'])
  const [openMenus, setOpenMenus] = React.useState(() => new Set()) // open submenu pathnames
  const [search, setSearch] = React.useState('')
  const [, setSelectedMenu] = React.useState(null)
  const {sidebarWidth, handleMouseEnter, setSidebarWidth, isHovered, handleMouseLeave, mobileOpen, setMobileOpen} =
    useSidebarToggle()
  // Phones and tablets get a slide-over menu: always full width, never the icon rail, and it
  // closes itself when a page is chosen.
  const isMobile = useIsMobileFrame()
  const railMode = !isMobile && sidebarWidth
  const drawerWidth = isMobile ? '248px' : isHovered ? '248px' : sidebarWidth ? '60px' : '248px'

  useEffect(() => {
    setMobileOpen(false)
  }, [pathname, setMobileOpen])

  useEffect(() => {
    if (Object.keys(userInfo).length === 0) {
      navigate('/login')
    }
  }, [navigate, userInfo])

  const onExit = () => {
    setEnabled(false)
  }

  useEffect(() => {
    const payload = {
      collection: 'users',
      andfilter: [
        {
          field: 'username',
          operator: 'eq',
          value: userName,
        },
      ],
      offset: 0,
      limit: 1,
    }
    if (VerifiedRole?.length > 0) {
      post(`${apiConstants.BASE_LOCAL}${apiConstants.GET_USER}`, payload)
        .then((response) => {
          if (response?.data?.data[0]?.logins?.pastLogins?.length <= 1) {
            setEnabled(true)
          }
        })
        .catch((err) => console.log(err))
    }
  }, [VerifiedRole?.length, userName])

  const steps = [
    {
      element: '#menu-main',
      title: 'Welcome to Proship',
      intro:
        'Let’s get you started! Use this menu to explore all the key features of your dashboard.',
    },
    {
      element: '#menu-dashboard',
      title: 'Home',
      intro:
        'View order insights, track delivery performance, and monitor RTOs — all in one place.',
      position: 'right',
    },
    {
      element: '#menu-control-tower',
      title: 'Control Tower',
      intro: 'View detailed analytics to monitor all shipments at one place.',
      position: 'right',
    },
    {
      element: '#menu-shipment',
      title: 'D2C Shipments',
      intro:
        'Create new shipments, manage bulk orders, generate labels, and track shipment progress with ease.',
      position: 'right',
    },
    {
      element: '#menu-ptl-shipment',
      title: 'PTL/Express Shipments',
      intro: 'Create and track PTL shipments, create pickup requests and generate shipping labels.',
      position: 'right',
    },
    {
      element: '#menu-rvp-shipment',
      title: 'RVP Shipments',
      intro: 'Manage and track all RVP shipments (QC & Non-QC) here.',
      position: 'right',
    },
    {
      element: '#menu-channel-orders',
      title: 'Webstore Orders',
      intro:
        'Integrate Proship directly with your wesbtores here. Easily generate shipping labels for your orders one at a time or in bulk',
      position: 'right',
    },
    {
      element: '#menu-ndr',
      title: 'NDR',
      intro: 'Resolve Non-Delivery Reports quickly and keep your customers updated without delays.',
      position: 'right',
    },
    {
      element: '#menu-wd',
      title: 'Weight Discrepancy',
      intro: 'Check for weight mismatches and resolve them directly from here.',
      position: 'right',
    },
    {
      element: '#menu-tools',
      title: 'Tools',
      intro: 'Check service availability by pin code or calculate shipment rates instantly.',
      position: 'right',
    },
    {
      element: '#menu-wallet',
      title: 'Finance',
      intro: 'Track wallet transactions and manage COD remittances in one place.',
      position: 'right',
    },
    {
      element: '#menu-help',
      title: 'Help',
      intro: 'Raise a ticket or find answers to your queries whenever you need support.',
    },
  ]

  const menuArray = getSidebarItems()
  // Merchant-level D2C/PTL toggles (admin panel > merchant details). Hide only when explicitly
  // disabled — missing/undefined on older merchant docs must still show both, same convention
  // as RVP-QC's showRvpQcToMerchant gate.
  const merchant = getMerchant()
  const ptlDisabled = merchant?.isPTLMerchant === false
  const d2cDisabled = merchant?.isD2CMerchant === false
  const isVerticalVisible = (m) => (m.vertical !== 'PTL' || !ptlDisabled) && (m.vertical !== 'D2C' || !d2cDisabled)

  // Active detection — longest-match-wins (path-boundary), so sibling routes that
  // share a prefix (e.g. /d2c-dashboard vs /d2c-dashboard/shipments) never both light
  // up. A plain pathname.includes() kept the first/prefix item highlighted everywhere.
  const matchesPath = (target) => !!target && (pathname === target || pathname.startsWith(target + '/'))
  let bestMatchUrl = ''
  menuArray.forEach((m) => {
    if (matchesPath(m.pathname) && m.pathname.length > bestMatchUrl.length) bestMatchUrl = m.pathname
    ;(m.menuItems || []).forEach((s) => {
      if (matchesPath(s.pathname) && s.pathname.length > bestMatchUrl.length) bestMatchUrl = s.pathname
    })
  })
  const isSubActive = (submenu) => submenu.pathname === bestMatchUrl
  // A parent header is highlighted when it IS the active page, or when it holds the active
  // child and the child isn't visible — i.e. its submenu is collapsed, OR the whole sidebar
  // is the icon-only rail (no submenus shown). When the submenu is open in the expanded
  // sidebar, the active child carries the highlight, not the header.
  const isIconRail = drawerWidth === '60px'
  const menuHighlight = (menu, isOpen) => {
    if (menu.pathname === bestMatchUrl) return true
    const hasActiveChild = (menu.menuItems || []).length > 0 && menu.menuItems.some((s) => s.pathname === bestMatchUrl)
    if (!hasActiveChild) return false
    return isIconRail || !isOpen
  }

  // Menu search runs over a flat index of every reachable page (routes/menuSearch.js) and
  // renders as a result list rather than a pruned tree. Filtering the tree in place could
  // only ever surface pages that have a sidebar entry — about two thirds of the app —
  // so tabs of consolidated pages and settings sub-pages were unfindable.
  const q = search.trim()
  const searchIndex = React.useMemo(() => buildMenuSearchIndex(), [])
  const searchMatches = React.useMemo(() => (q ? searchMenu(searchIndex, q) : []), [q, searchIndex])
  // Results carry the same visibility gates the tree applies — roles, email domain, merchant
  // config and the D2C/PTL vertical toggles — so search can't route a merchant to a page
  // their sidebar deliberately hides. Gating stays outside the memo: it's a cheap filter, and
  // isVerticalVisible closes over per-render merchant state.
  const searchResults = searchMatches.filter(
    (r) =>
      isAuthorized(r.roles) &&
      isEmailDomainAllowed(r.allowedEmailDomains) &&
      isVerticalVisible(r) &&
      (!r.requiresDirectPlan || directPlanActive) &&
      r.configs.every((c) => isConfigRouteVisible(c, orderConversionDetails))
  )

  const handleSubMenuClick = (menuUrl) => {
    setOpenMenus((prev) => {
      const next = new Set(prev)
      if (next.has(menuUrl)) next.delete(menuUrl)
      else next.add(menuUrl)
      return next
    })
  }

  // Expand All / Collapse All (like the admin panel).
  const allSubMenuPaths = menuArray.filter((m) => m?.menuItems?.length > 0).map((m) => m.pathname)
  const allExpanded = allSubMenuPaths.length > 0 && allSubMenuPaths.every((p) => openMenus.has(p))
  const expandAll = () => setOpenMenus(new Set(allSubMenuPaths))
  const collapseAll = () => setOpenMenus(new Set())

  const setSelectedMenuBasedOnUrl = () => {
    const foundMenu = menuArray.find(
      (menu) =>
        pathname === menu.pathname ||
        (menu.menuItems && menu.menuItems.some((submenu) => pathname === submenu.pathname))
    )
    setSelectedMenu(foundMenu)
    // auto-open the active parent's submenu
    if (foundMenu?.menuItems?.length > 0) {
      setOpenMenus((prev) => new Set(prev).add(foundMenu.pathname))
    }
  }

  React.useEffect(() => {
    setSelectedMenuBasedOnUrl()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname])

  // The search field is hidden on the icon-only rail. Drop the query with it, otherwise
  // the rail stays silently filtered with no visible input to clear.
  React.useEffect(() => {
    if (isIconRail) setSearch('')
  }, [isIconRail])

  return (
    <React.Fragment>
      <Steps
        enabled={enabled}
        steps={steps}
        initialStep={initialStep}
        onExit={() => onExit()}
        options={{doneLabel}}
      />

      <StyledDrawer
        variant={isMobile ? 'temporary' : 'persistent'}
        anchor='left'
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{keepMounted: true}}
        drawerwidth={drawerWidth}
      >
        {/* Hover-to-expand is bound to the whole list, not just the menu items: the search
            field sits above them, so handlers scoped to MenuItemsContainer collapsed the
            rail — and unmounted the field — the moment the pointer moved up to type. */}
        <StyledList onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
          {/* Header with logo and toggle button - no hover effect */}
          <HeaderListItem>
            {!railMode && (
              <img
                src='/img/prozo-logo3.svg'
                height={25}
                alt='logo'
                id='home'
                style={{margin: '0 auto'}}
              />
            )}
            {railMode ? (
              <ToggleIcon
                onClick={() => {
                  setSidebarWidth(false)
                }}
              >
                <KeyboardDoubleArrowRightIcon />
              </ToggleIcon>
            ) : (
              <ToggleIcon
                onClick={() => {
                  if (isMobile) setMobileOpen(false)
                  else setSidebarWidth(true)
                }}
              >
                <KeyboardDoubleArrowLeftIcon />
              </ToggleIcon>
            )}
          </HeaderListItem>

          {/* Divider under the logo (matches admin) */}
          <Box sx={{borderTop: `1px solid ${SIDEBAR_LINE}`, mx: 1}} />

          {/* Expand All + menu search — only when the sidebar is expanded */}
          {drawerWidth !== '60px' && (
            <Box sx={{px: 1.5, pt: 1.5, pb: 0.5}}>
              {/* Expand all / Collapse all — meaningless while showing a flat result list */}
              <Box
                onClick={allExpanded ? collapseAll : expandAll}
                sx={{
                  display: q ? 'none' : 'flex',
                  alignItems: 'center', justifyContent: 'center', gap: 0.5,
                  mb: 1, py: 0.5, borderRadius: '8px', cursor: 'pointer',
                  border: `1px solid ${SIDEBAR_LINE}`, color: SIDEBAR_TEXT,
                  '&:hover': {bgcolor: SIDEBAR_HOVER_BG, color: SIDEBAR_ACCENT},
                }}
              >
                {allExpanded ? <UnfoldLess sx={{fontSize: 16}} /> : <UnfoldMore sx={{fontSize: 16}} />}
                <Typography sx={{fontSize: 11, fontWeight: 400, fontFamily: 'Poppins, sans-serif', letterSpacing: '0.02em'}}>
                  {allExpanded ? 'Collapse All' : 'Expand All'}
                </Typography>
              </Box>

              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 0.75,
                bgcolor: 'rgba(145,158,171,0.08)', border: '1px solid rgba(145,158,171,0.2)',
                borderRadius: '8px', px: 1, height: 36,
                '&:focus-within': {borderColor: SIDEBAR_ACCENT},
              }}>
                <SearchIcon sx={{fontSize: 18, color: '#637381'}} />
                <InputBase
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Escape' && setSearch('')}
                  placeholder='Search menu...'
                  sx={{flex: 1, fontSize: 12, color: '#fff', '& input::placeholder': {color: '#637381', opacity: 1}}}
                />
                {search && (
                  <CloseIcon onClick={() => setSearch('')} sx={{fontSize: 16, color: '#637381', cursor: 'pointer'}} />
                )}
              </Box>
            </Box>
          )}

          {/* Search results replace the tree entirely while a query is active */}
          <MenuItemsContainer className='menu-items' drawerwidth={drawerWidth}>
            {q && (
              <React.Fragment>
                <SectionLabel drawerwidth={drawerWidth}>
                  <SectionLabelText>
                    {searchResults.length > 0 ? `${searchResults.length} RESULT${searchResults.length > 1 ? 'S' : ''}` : 'NO RESULTS'}
                  </SectionLabelText>
                </SectionLabel>
                {searchResults.length === 0 ? (
                  <Typography
                    sx={{
                      px: 2, py: 1, fontSize: 11, color: '#78838f',
                      fontFamily: 'Poppins, sans-serif', lineHeight: 1.5,
                    }}
                  >
                    Nothing matches “{q}”. Try a shorter word — or clear the search to browse the menu.
                  </Typography>
                ) : (
                  searchResults.map((result) => (
                    <ResultItem
                      button
                      key={result.pathname}
                      component={Link}
                      to={result.pathname}
                      onClick={() => setSearch('')}
                    >
                      <MainMenuIcon>{result.icon || <FiberManualRecord sx={{fontSize: 5}} />}</MainMenuIcon>
                      <MenuText
                        primary={result.label}
                        secondary={result.parentLabel}
                        primaryTypographyProps={{fontSize: 11, fontWeight: 400}}
                        secondaryTypographyProps={{fontSize: 9, color: '#78838f'}}
                      />
                      {result.badge && <BadgeSpan>{result.badge}</BadgeSpan>}
                    </ResultItem>
                  ))
                )}
              </React.Fragment>
            )}
            {!q && SIDEBAR_GROUPS.map((group) => {
              const groupItems = menuArray.filter((m) => m.group === group.key)
              if (groupItems.length === 0) return null
              const visibleItems = groupItems.filter(
                (m) =>
                  isAuthorized(m.roles) &&
                  isEmailDomainAllowed(m.allowedEmailDomains) &&
                  isVerticalVisible(m) &&
                  (!m.requiresDirectPlan || directPlanActive) &&
                  isConfigRouteVisible(m.enableForConfig, orderConversionDetails)
              )
              if (visibleItems.length === 0) return null
              return (
                <React.Fragment key={group.key}>
                  <SectionLabel drawerwidth={drawerWidth}>
                    <SectionLabelText>
                      {/* GENERAL is back to its own name. It was relabelled
                          "PERFORMANCE & ANALYTICS" while Prozo Direct hid the Home entry and
                          left the group holding only analytics; Home is in it again, and a
                          group containing Home is not an analytics section. */}
                      {group.label}
                    </SectionLabelText>
                  </SectionLabel>
                  {visibleItems.map((menu) => {
                    const hasSubMenu = menu?.menuItems?.length > 0
                    const isSubMenuOpen = openMenus.has(menu.pathname)
                    const isMenuActive = menuHighlight(menu, isSubMenuOpen)

                    return (
                      <React.Fragment key={menu.pathname}>
                        <MainMenuItem
                          button
                          component={hasSubMenu ? 'li' : Link}
                          to={hasSubMenu ? null : menu.pathname}
                          onClick={() => hasSubMenu && handleSubMenuClick(menu.pathname)}
                          id={menu?.id || null}
                          isactive={isMenuActive}
                          isselected={isMenuActive}
                        >
                          {menu.icon && (
                            <MainMenuIcon
                              isactive={isMenuActive}
                              isselected={isMenuActive}
                            >
                              {menu.icon}
                            </MainMenuIcon>
                          )}
                          {!isIconRail && menu.label && (
                            <MenuText primary={menu.label} primaryTypographyProps={{fontSize: 11, fontWeight: 400}} />
                          )}
                          {!isIconRail && hasSubMenu && (
                            <ExpandIcon onClick={() => handleSubMenuClick(menu.pathname)}>
                              {isSubMenuOpen ? (
                                <ExpandLess fontSize='smallest' />
                              ) : (
                                <ExpandMore fontSize='smallest' />
                              )}
                            </ExpandIcon>
                          )}
                        </MainMenuItem>
                        {((isHovered && hasSubMenu) || (hasSubMenu && !railMode)) && (
                          <Collapse in={isSubMenuOpen} timeout='auto' unmountOnExit>
                            <SubMenuList component='div' disablePadding>
                              {menu.menuItems
                                .filter((submenu) => !submenu.hideInSidebar)
                                .filter(
                                  (submenu) => isConfigRouteVisible(submenu.enableForConfig, orderConversionDetails)
                                )
                                .map((submenu) => (
                                  <SubMenuItem
                                    button
                                    key={submenu.label}
                                    component={Link}
                                    to={submenu.pathname}
                                    isactive={isSubActive(submenu)}
                                  >
                                    <ListItemIcon sx={{minWidth: 0, mr: 1.5, justifyContent: 'center', color: 'inherit'}}>
                                      <FiberManualRecord sx={{fontSize: 4}} />
                                    </ListItemIcon>
                                    <SubMenuText
                                      primary={submenu.label}
                                      secondary={
                                        submenu.badge ? <BadgeSpan>{submenu.badge}</BadgeSpan> : null
                                      }
                                      primaryTypographyProps={{fontSize: 11, fontWeight: 400}}
                                    />
                                  </SubMenuItem>
                                ))}
                            </SubMenuList>
                          </Collapse>
                        )}
                      </React.Fragment>
                    )
                  })}
                </React.Fragment>
              )
            })}
            {!q && menuArray.filter((m) => !m.group && isAuthorized(m.roles) && isEmailDomainAllowed(m.allowedEmailDomains) && isVerticalVisible(m) && isConfigRouteVisible(m.enableForConfig, orderConversionDetails)).map((menu) => {
              const hasSubMenu = menu?.menuItems?.length > 0
              const isSubMenuOpen = openMenus.has(menu.pathname)
              const isMenuActive = menuHighlight(menu, isSubMenuOpen)

              return (
                <React.Fragment key={menu.pathname}>
                  <MainMenuItem
                    button
                    component={hasSubMenu ? 'li' : Link}
                    to={hasSubMenu ? null : menu.pathname}
                    onClick={() => hasSubMenu && handleSubMenuClick(menu.pathname)}
                    id={menu?.id || null}
                    isactive={isMenuActive}
                    isselected={isMenuActive}
                  >
                    {menu.icon && (
                      <MainMenuIcon
                        isactive={isMenuActive}
                        isselected={isMenuActive}
                      >
                        {menu.icon}
                      </MainMenuIcon>
                    )}
                    {!isIconRail && menu.label && (
                      <MenuText primary={menu.label} primaryTypographyProps={{fontSize: 11, fontWeight: 400}} />
                    )}
                    {!isIconRail && hasSubMenu && (
                      <ExpandIcon onClick={() => handleSubMenuClick(menu.pathname)}>
                        {isSubMenuOpen ? (
                          <ExpandLess fontSize='smallest' />
                        ) : (
                          <ExpandMore fontSize='smallest' />
                        )}
                      </ExpandIcon>
                    )}
                  </MainMenuItem>
                  {((isHovered && hasSubMenu) || (hasSubMenu && !railMode)) && (
                    <Collapse in={isSubMenuOpen} timeout='auto' unmountOnExit>
                      <SubMenuList component='div' disablePadding>
                        {menu.menuItems
                          .filter((submenu) => !submenu.hideInSidebar)
                          .filter(
                            (submenu) => isConfigRouteVisible(submenu.enableForConfig, orderConversionDetails)
                          )
                          .map((submenu) => (
                            <SubMenuItem
                              button
                              key={submenu.label}
                              component={Link}
                              to={submenu.pathname}
                              isactive={isSubActive(submenu)}
                            >
                              <ListItemIcon sx={{minWidth: 0, mr: 1.5, justifyContent: 'center', color: 'inherit'}}>
                                <FiberManualRecord sx={{fontSize: 4}} />
                              </ListItemIcon>
                              <SubMenuText
                                primary={submenu.label}
                                secondary={
                                  submenu.badge ? <BadgeSpan>{submenu.badge}</BadgeSpan> : null
                                }
                                primaryTypographyProps={{fontSize: 11, fontWeight: 400}}
                              />
                            </SubMenuItem>
                          ))}
                      </SubMenuList>
                    </Collapse>
                  )}
                </React.Fragment>
              )
            })}
          </MenuItemsContainer>

          <Box sx={{height: 80}} />
        </StyledList>
      </StyledDrawer>
    </React.Fragment>
  )
}

export default Sidebar
